import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { FC, useContext, useEffect, useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { RepairOrderContext } from '../../../providers/RepairOrderProvider/RepairOrderProvider'
import { useGetReviewsQuery } from '../../../redux/api/feedbackApi'
import SendFeedbackLinkForm from './SendFeedbackLinkForm'

interface Props {
  open: boolean
  handleClose: () => void
}

const COLUMNS: GridColDef[] = [
  { field: 'datetime', headerName: 'Date', type: 'dateTime', width: 150 },
  {
    field: 'stars',
    headerName: 'Rating (1-5)',
    type: 'number',
    width: 125
  },
  {
    field: 'warranty',
    headerName: 'Warranty Service',
    type: 'boolean',
    width: 150
  },
  {
    field: 'timely',
    headerName: 'Timely Service',
    type: 'boolean',
    width: 175
  },
  {
    field: 'courteous',
    headerName: 'Courteous Staff',
    type: 'boolean',
    width: 150
  },
  {
    field: 'informed',
    headerName: 'Customer Informed',
    type: 'boolean',
    width: 175
  },
  {
    field: 'completed',
    headerName: 'Work Completed',
    type: 'boolean',
    width: 175
  }
]

const FeedbackListDialog: FC<Props> = ({ open, handleClose }) => {
  const { repairOrder } = useContext(RepairOrderContext)
  const { accessToken } = useAccessTokenAndShopId()
  const {
    data: reviews,
    isLoading,
    refetch: refetchReviews
  } = useGetReviewsQuery(
    {
      authorization: accessToken,
      repairOrderId: repairOrder?.entry?.id!
    },
    { skip: !repairOrder?.entry?.id }
  )
  const [formOpen, setFormOpen] = useState<boolean>(false)

  useEffect(() => {
    if (open) {
      refetchReviews()
    }
  }, [refetchReviews, open])

  const rows = useMemo(() => {
    if (!reviews) {
      return []
    }
    return reviews.map(({ apiKey, dateTime, info }) => ({
      id: apiKey,
      datetime: dateTime,
      ...info
    }))
  }, [reviews])

  const handleBackToGrid: () => void = () => setFormOpen(false)

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Customer Feedback</DialogTitle>
      {formOpen ? (
        <SendFeedbackLinkForm
          cancelCallback={handleBackToGrid}
          postSubmitCallback={handleBackToGrid}
        />
      ) : (
        <>
          <DialogContent sx={{ height: 350 }}>
            <DataGridPro
              loading={isLoading}
              columns={COLUMNS}
              rows={rows}
              initialState={{
                sorting: { sortModel: [{ field: 'datetime', sort: 'desc' }] }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setFormOpen(true)}>
              Send Link
            </Button>
            <Button variant="contained" onClick={handleClose}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default FeedbackListDialog
